import { useRef, useEffect, useState } from "react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { sessionQueryOptions } from "@/api-queries/auth";
import { useQuery } from "@tanstack/react-query";
import { User as UserIcon, Calendar, Shield, UserGear, Star } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { formatDateWithOptionsBs } from "@/lib/format";
import { ClientSessionsPanel } from "@/components/client/ClientSessionsPanel";
import { CompanyInvitationsPanel } from "@/components/client/CompanyInvitationsPanel";
import { AccountForm } from "@/components/client/AccountForm";
import { SecurityForm } from "@/components/client/SecurityForm";
import { MyReviewsSection } from "@/components/reviews/MyReviewsSection";

type ProfileTab = "sessions" | "reviews" | "account" | "security";

interface SessionUser {
  id: string;
  name: string;
  email: string;
  image?: string | null;
  createdAt?: string;
  role?: string;
}

export const Route = createFileRoute("/profile")({
  validateSearch: (search: Record<string, unknown>): { tab: ProfileTab } => {
    const tab = search.tab;
    return {
      tab:
        tab === "account" ||
        tab === "security" ||
        tab === "sessions" ||
        tab === "reviews"
          ? tab
          : "sessions",
    };
  },
  beforeLoad: async () => {
    const res = await fetch("/api/auth/get-session");
    if (!res.ok) throw redirect({ to: "/login" });
    const session = await res.json();
    if (!session?.user) throw redirect({ to: "/login" });
    if (session.user.role !== "user" && session.user.role !== "client") {
      throw redirect({ to: "/dashboard" });
    }
    return { user: session.user as SessionUser };
  },
  component: ProfilePage,
});

function ProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tab } = Route.useSearch();
  const { user: initialUser } = Route.useRouteContext();

  const { data: session } = useQuery(sessionQueryOptions());
  const user = (session?.user ?? initialUser) as SessionUser;

  if (!session?.user) return null;

  const setTab = (next: ProfileTab) => {
    navigate({ to: "/profile", search: { tab: next }, replace: true });
  };

  const tabs: { value: ProfileTab; label: string; icon: typeof Calendar }[] = [
    { value: "sessions", label: t("profile.tabs.sessions"), icon: Calendar },
    { value: "reviews", label: t("profile.tabs.reviews", { defaultValue: "Recenzije" }), icon: Star },
    { value: "account", label: t("profile.tabs.account"), icon: UserGear },
    { value: "security", label: t("profile.tabs.security"), icon: Shield },
  ];

  const memberSince = user.createdAt
    ? formatDateWithOptionsBs(user.createdAt, {
        year: "numeric",
        month: "long",
        timeZone: "Europe/Sarajevo",
      })
    : null;

  const initial = (user.name || user.email || "?").trim().charAt(0).toUpperCase();

  return (
    <div key={user.id} className="relative min-h-[100svh] overflow-hidden">
      {/* Ambient background — mirrors Hero aesthetic */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(180deg, var(--color-section-bg) 0%, oklch(96% 0.015 84) 45%, oklch(98% 0.005 84) 100%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-48 -z-10 h-[420px] w-[420px] rounded-full opacity-[0.18] blur-3xl"
        style={{ background: "var(--color-brand-bg)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute top-[40%] -left-40 -z-10 h-[300px] w-[300px] rounded-full opacity-[0.12] blur-3xl"
        style={{ background: "oklch(49.83% 0.0663 186.62 / 0.15)" }}
      />

      <div className="mx-auto max-w-5xl px-4 pt-10 pb-16 sm:px-6 sm:pt-14 lg:px-8">
        {/* Hero header */}
        <header className="animate-[fadeUp_0.6s_ease-out_both]">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:gap-8">
            {/* Avatar */}
            <div className="relative shrink-0">
              <div
                className="relative size-24 overflow-hidden rounded-full bg-white sm:size-28"
                style={{
                  boxShadow:
                    "0 4px 14px oklch(37.12% 0.059 186.62 / 0.12), 0 0 0 1px var(--color-warm-border)",
                }}
              >
                {user.image ? (
                  <img
                    src={user.image}
                    alt=""
                    className="size-full object-cover"
                  />
                ) : (
                  <div
                    className="flex size-full items-center justify-center text-3xl font-semibold"
                    style={{
                      background:
                        "linear-gradient(135deg, var(--color-brand-bg), oklch(49.83% 0.0663 186.62 / 0.15))",
                      color: "var(--color-brand-dark)",
                      fontFamily: "var(--font-heading)",
                    }}
                  >
                    {initial}
                  </div>
                )}
              </div>
            </div>

            {/* Name + meta */}
            <div className="min-w-0 flex-1">
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]"
                style={{
                  background:
                    "linear-gradient(135deg, var(--color-brand-bg-light), oklch(49.83% 0.0663 186.62 / 0.08))",
                  border: "1px solid var(--color-brand-bg)",
                  color: "var(--color-brand-muted)",
                }}
              >
                {t("profile.tabs.account")}
              </span>
              <h1
                className="font-heading mt-3 text-3xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-4xl md:text-5xl"
              >
                {user.name ? (
                  <>
                    <span
                      className="inline-block"
                      style={{
                        background:
                          "linear-gradient(135deg, var(--color-section-label), var(--color-brand-dark), oklch(60% 0.16 60))",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      {user.name}
                    </span>
                  </>
                ) : (
                  t("profile.untitled")
                )}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <UserIcon className="size-3.5" />
                  {user.email}
                </span>
                {memberSince && (
                  <>
                    <span
                      aria-hidden
                      className="hidden h-1 w-1 rounded-full bg-muted-foreground/40 sm:inline-block"
                    />
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="size-3.5" />
                      {t("profile.memberSince", { date: memberSince })}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        <CompanyInvitationsPanel userId={user.id} />

        {/* Tab bar */}
        <nav
          className="mt-10 animate-[fadeUp_0.6s_ease-out_0.08s_both] sm:mt-14"
          aria-label={t("profile.tabs.account")}
        >
          <TabBar tabs={tabs} activeTab={tab} onChange={setTab} />
        </nav>

        {/* Panel */}
        <section
          key={tab}
          className="mt-8 animate-[fadeUp_0.45s_ease-out_both]"
        >
          {tab === "sessions" && <ClientSessionsPanel />}
          {tab === "reviews" && <MyReviewsSection />}
          {tab === "account" && <AccountForm user={user} />}
          {tab === "security" && <SecurityForm />}
        </section>
      </div>

      {/* Local keyframes — scoped via unique name */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

interface TabBarProps {
  tabs: { value: ProfileTab; label: string; icon: typeof Calendar }[];
  activeTab: ProfileTab;
  onChange: (value: ProfileTab) => void;
}

function TabBar({ tabs, activeTab, onChange }: TabBarProps) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  useEffect(() => {
    const el = btnRefs.current[activeTab];
    const list = listRef.current;
    if (!el || !list) return;
    const elRect = el.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    setIndicator({
      left: elRect.left - listRect.left + list.scrollLeft,
      width: elRect.width,
    });
    // Keep active tab visible on mobile
    const needsScrollRight = elRect.right > listRect.right;
    const needsScrollLeft = elRect.left < listRect.left;
    if (needsScrollRight || needsScrollLeft) {
      el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [activeTab]);

  return (
    <div className="relative">
      <div
        ref={listRef}
        className="no-scrollbar relative flex gap-1 overflow-x-auto rounded-2xl p-1.5"
        style={{
          background: "oklch(100% 0 0 / 0.7)",
          border: "1px solid var(--color-warm-border)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          boxShadow: "0 1px 2px oklch(0% 0 0 / 0.02)",
        }}
      >
        {indicator && (
          <span
            aria-hidden
            className="absolute top-1.5 bottom-1.5 rounded-xl transition-all duration-300 ease-out"
            style={{
              left: indicator.left,
              width: indicator.width,
              background: "var(--color-brand-bg-light)",
              border: "1px solid var(--color-brand-bg)",
            }}
          />
        )}
        {tabs.map((item) => {
          const Icon = item.icon;
          const active = activeTab === item.value;
          return (
            <button
              key={item.value}
              ref={(el) => {
                btnRefs.current[item.value] = el;
              }}
              onClick={() => onChange(item.value)}
              className={cn(
                "relative z-10 flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-medium transition-colors duration-200",
                active
                  ? "text-[color:var(--color-brand-dark)]"
                  : "text-muted-foreground hover:text-foreground"
              )}
              aria-pressed={active}
            >
              <Icon className="size-4" />
              {item.label}
            </button>
          );
        })}
      </div>

      {/* Edge fade for mobile overflow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 w-8 rounded-r-2xl sm:hidden"
        style={{
          background:
            "linear-gradient(to left, var(--color-section-bg), transparent)",
        }}
      />

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { scrollbar-width: none; }
      `}</style>
    </div>
  );
}
